import { useEffect, useState } from "react";
import { samplePreview, type PfpProject } from "@/pfp/generator";

export function RollPreview({
  project,
  seed,
  onRoll,
}: {
  project: PfpProject;
  seed: number;
  onRoll: () => void;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setGenerating(true);
    setError(null);
    void (async () => {
      try {
        // 384px is enough for a sharp-looking sidebar preview without
        // pinning the full canvas size in memory.
        const result = await samplePreview(project, { seed, targetSize: 384 });
        if (cancelled) return;
        if (!result) {
          setPreview(null);
          if (project.layers.length > 0) {
            setError("Rules are too tight — no valid combination found.");
          }
        } else {
          setPreview(result.dataUrl);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project, seed]);

  const empty = project.layers.length === 0 || project.layers.every((l) => l.traits.length === 0);

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="aspect-square w-full overflow-hidden rounded-md border border-border bg-muted">
        {preview ? (
          <img src={preview} alt="Sample preview" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center px-3 text-center text-xs text-muted-foreground">
            {empty ? "Add layers and traits to roll a preview." : generating ? "Rolling…" : error ?? "—"}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRoll}
        disabled={empty || generating}
        className="btn-primary inline-flex w-full items-center justify-center gap-2"
      >
        <span aria-hidden="true">🎲</span>
        Roll
      </button>
    </div>
  );
}
